import { useState } from 'react';
import { fixed } from '../math/format';
import type { McQuestion, NumericQuestion, SliderQuestion } from '../content/types';

// ── Multiple choice ──────────────────────────────────────────────────────────

interface McProps {
  readonly question: McQuestion;
  readonly picked: number | null;
  readonly revealed: boolean;
  readonly onPick: (index: number) => void;
}

export function McList({ question, picked, revealed, onPick }: McProps) {
  return (
    <div role="radiogroup" style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      {question.options.map((option, i) => {
        const isAnswer = revealed && i === question.answerIndex;
        const isWrong = revealed && i === picked && i !== question.answerIndex;
        const isSelected = !revealed && i === picked;
        // Options that are neither picked nor correct fade back once revealed,
        // so the eye lands on the comparison that matters.
        const isMuted = revealed && !isAnswer && !isWrong;

        return (
          <button
            key={option.label}
            type="button"
            role="radio"
            aria-checked={i === picked}
            onClick={() => onPick(i)}
            disabled={revealed}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              minHeight: 56,
              padding: '15px',
              borderRadius: 13,
              width: '100%',
              textAlign: 'left',
              cursor: revealed ? 'default' : 'pointer',
              transition: 'border-color var(--motion), background var(--motion), opacity var(--motion)',
              background: isAnswer
                ? 'oklch(0.82 0.17 130 / .16)'
                : isWrong
                  ? 'var(--red-dim)'
                  : isSelected
                    ? 'oklch(0.82 0.17 130 / .1)'
                    : 'var(--surface-2)',
              border: `1px solid ${
                isAnswer || isSelected
                  ? 'var(--lime)'
                  : isWrong
                    ? 'var(--red)'
                    : 'var(--line-strong)'
              }`,
              color: 'var(--text)',
              opacity: isMuted ? 0.45 : 1,
            }}
          >
            <span
              style={{
                flex: 'none',
                width: 26,
                height: 26,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                font: '700 12px/1 var(--font-display)',
                background: 'rgba(255,255,255,.07)',
                color: 'var(--text-3)',
              }}
            >
              {'ABCD'[i] ?? i + 1}
            </span>
            <span
              className="num"
              style={{ flex: 1, font: '600 16px/1.3 var(--font-display)' }}
            >
              {option.label}
            </span>
            <span
              style={{
                flex: 'none',
                width: 18,
                font: '700 15px/1 var(--font-display)',
                color: isAnswer ? 'var(--lime)' : 'var(--red)',
              }}
            >
              {isAnswer ? '✓' : isWrong ? '✕' : ''}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ── Slider ───────────────────────────────────────────────────────────────────

interface SliderProps {
  readonly question: SliderQuestion;
  readonly value: number | null;
  readonly revealed: boolean;
  readonly onChange: (value: number) => void;
}

export function SliderInput({ question, value, revealed, onChange }: SliderProps) {
  const current = value ?? (question.min + question.max) / 2;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: 16,
        borderRadius: 14,
        background: 'var(--surface-3)',
        border: '1px solid var(--line-strong)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div
          className="num"
          style={{ font: '700 30px/1 var(--font-display)', letterSpacing: '-.02em' }}
        >
          {fixed(current, 3)}
          {question.unit}
        </div>
        {question.convert && (
          <div className="num" style={{ font: '500 12px/1 var(--font-body)', color: 'var(--text-4)' }}>
            {question.convert(current)}
          </div>
        )}
      </div>
      <input
        type="range"
        min={question.min}
        max={question.max}
        step={question.stepSize}
        value={current}
        disabled={revealed}
        onChange={(e) => onChange(Number.parseFloat(e.target.value))}
        aria-label={question.prompt}
        style={{ width: '100%', accentColor: 'var(--lime)', height: 32 }}
      />
      <div
        className="num"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          font: '500 11px/1 var(--font-body)',
          color: 'var(--text-4)',
        }}
      >
        <div>
          {fixed(question.min, 1)}
          {question.unit}
        </div>
        <div>
          {fixed(question.max, 1)}
          {question.unit}
        </div>
      </div>
    </div>
  );
}

// ── Numeric entry ────────────────────────────────────────────────────────────

interface NumericProps {
  readonly question: NumericQuestion;
  readonly value: number | null;
  readonly revealed: boolean;
  readonly onChange: (value: number | null) => void;
}

/**
 * Free numeric entry.
 *
 * The raw text is held locally rather than derived from the parsed number.
 * Deriving it means "0." parses to null, re-renders as "", and the field wipes
 * itself the moment she types a decimal point. Mount this with a key that
 * changes per question so the text resets between questions.
 */
export function NumericInput({ question, value, revealed, onChange }: NumericProps) {
  const [text, setText] = useState(value === null ? '' : String(value));

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '6px 16px',
        borderRadius: 14,
        background: 'var(--surface-3)',
        border: '1px solid var(--line-strong)',
      }}
    >
      <input
        // `decimal` rather than `number`: it brings up a numeric keypad on a
        // phone without the spinner arrows, which are useless with gloves on.
        inputMode="decimal"
        type="text"
        value={text}
        disabled={revealed}
        placeholder={`0.${'0'.repeat(question.places)}`}
        aria-label={question.prompt}
        onChange={(e) => {
          const raw = e.target.value;
          // Digits, one leading minus, one point. Anything else is ignored so
          // a stray character cannot silently blank a half-typed answer.
          if (raw !== '' && !/^-?\d*\.?\d*$/.test(raw)) return;
          setText(raw);
          const parsed = Number.parseFloat(raw);
          onChange(Number.isFinite(parsed) ? parsed : null);
        }}
        className="num"
        style={{
          flex: 1,
          minHeight: 56,
          background: 'transparent',
          border: 'none',
          outline: 'none',
          color: 'var(--text)',
          font: '700 28px/1 var(--font-display)',
          letterSpacing: '-.02em',
          width: '100%',
        }}
      />
      <div style={{ font: '600 18px/1 var(--font-display)', color: 'var(--text-4)' }}>
        {question.unit}
      </div>
    </div>
  );
}
